import { NgClass } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '@work-frontend-monorepo/work-environments';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { PrependServerToFileSrcPipe } from '../../pipes/prepend-server-to-file-src.pipe';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

/**
 * Alles, was der Viewer von einem Dokument braucht.
 *
 * Bewusst ein Struktur-Typ und nicht `WorkDocument`: dieselbe Komponente rendert auch die
 * redigierten Dokument-DTOs der externen Projektfreigabe, die absichtlich nur eine
 * Teilmenge der Felder tragen. `WorkDocument` erfüllt diesen Typ, bestehende Aufrufer bleiben
 * unverändert.
 */
export interface ViewableDocument {
  name?: string | null;
  mimetype?: string | null;
  fileSrc?: string | null;
}

const OFFICE_MIME_TYPES = new Set<string>([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/vnd.ms-powerpoint', // .ppt
]);

const PREVIEW_READY_DELAY_MS = 500;
const PDF_VIEWER_REMOUNT_DELAY_MS = 100;

@Component({
  selector: 'work-ui-dokumente-viewer',
  templateUrl: './dokumente-viewer.component.html',
  styleUrls: ['./dokumente-viewer.component.scss'],
  standalone: true,
  imports: [
    LoadingSpinnerComponent,
    NgxExtendedPdfViewerModule,
    IconComponent,
    TranslateModule,
    PrependServerToFileSrcPipe,
    NgClass,
    ButtonComponent,
  ],
})
export class DokumenteViewerComponent {
  private readonly prependServerToFileSrcPipe = inject(PrependServerToFileSrcPipe);
  private readonly destroyRef = inject(DestroyRef);

  public readonly activeDocument = input.required<ViewableDocument | undefined | null>();
  public readonly renderPdfViewer = input.required<boolean>();

  protected readonly activeImageUrl = signal('');
  protected readonly activeDocumentUrl = signal('');
  protected readonly activeVideoUrl = signal('');
  protected readonly activeAudioUrl = signal('');
  protected readonly previewReady = signal(false);
  protected readonly isPdfViewerVisible = signal(false);
  protected readonly showFileLoadingException = signal(false);
  /**
   * Download/Speichern/Drucken im Viewer greift auf Seiteninfos zu, die erst nach dem Laden existieren
   * (sonst `TypeError: Cannot read properties of null (reading 'every')`). Bis dahin gesperrt.
   */
  protected readonly isPdfLoaded = signal(false);

  protected readonly isImage = computed(() => !!this.activeDocument()?.mimetype?.startsWith('image/'));
  protected readonly isVideo = computed(() => !!this.activeDocument()?.mimetype?.startsWith('video/'));
  protected readonly isAudio = computed(() => !!this.activeDocument()?.mimetype?.startsWith('audio/'));
  protected readonly isOfficeDocument = computed(() => {
    const mimetype = this.activeDocument()?.mimetype;
    return !!mimetype && OFFICE_MIME_TYPES.has(mimetype);
  });

  protected readonly environment = environment;

  private readonly pendingTimeouts = new Set<ReturnType<typeof setTimeout>>();

  private readonly documentChangeEffect = effect(() => {
    const document = this.activeDocument();
    if (!document) {
      return;
    }

    untracked(() => {
      this.previewReady.set(!this.showFileLoadingException());
      this.showFileLoadingException.set(false);

      if (this.isImage()) {
        this.activeImageUrl.set(document.fileSrc || '');
        this.schedulePreviewReady();
        return;
      }
      if (this.isVideo()) {
        this.activeVideoUrl.set(this.prependServerToFileSrcPipe.transform(document.fileSrc || ''));
        this.schedulePreviewReady();
        return;
      }
      if (this.isAudio()) {
        this.activeAudioUrl.set(this.prependServerToFileSrcPipe.transform(document.fileSrc || ''));
        this.schedulePreviewReady();
        return;
      }
      if (this.isOfficeDocument()) {
        this.schedulePreviewReady();
        return;
      }
      if (this.renderPdfViewer()) {
        const nextUrl = this.prependServerToFileSrcPipe.transform(document.fileSrc || '');
        if (nextUrl !== this.activeDocumentUrl()) {
          // Der Viewer bleibt beim Dokumentwechsel gemountet: Download sperren, bis das neue PDF geladen ist.
          this.isPdfLoaded.set(false);
        }
        this.activeDocumentUrl.set(nextUrl);
        if (!this.isPdfViewerVisible()) {
          this.togglePdfViewer(true);
        }
      }
    });
  });

  private readonly renderPdfChangeEffect = effect(() => {
    const render = this.renderPdfViewer();

    untracked(() => {
      if (!this.activeDocument()) {
        return;
      }
      if (!render) {
        this.previewReady.set(false);
      }
      this.togglePdfViewer(false);
      if (render && (this.isImage() || this.isVideo() || this.isAudio() || this.isOfficeDocument())) {
        this.togglePdfViewer(true);
      }
    });
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.pendingTimeouts.forEach((handle) => clearTimeout(handle));
      this.pendingTimeouts.clear();
    });
  }

  protected onPdfLoaded() {
    this.isPdfLoaded.set(true);
    this.schedulePreviewReady();
  }

  protected onFileLoadException() {
    this.isPdfLoaded.set(false);
    this.showFileLoadingException.set(true);
    this.schedulePreviewReady();
  }

  protected schedulePreviewReady() {
    this.scheduleTimeout(() => {
      if (!this.previewReady()) {
        this.previewReady.set(true);
      }
    }, PREVIEW_READY_DELAY_MS);
  }

  protected openInNewTab() {
    const fileSrc = this.activeDocument()?.fileSrc;
    if (!fileSrc) {
      return;
    }
    window.open(this.prependServerToFileSrcPipe.transform(fileSrc), '_blank');
  }

  /**
   * Only one pdf viewer instance can be on the page at once, therefore wait for the old one to be removed first.
   */
  private togglePdfViewer(show: boolean) {
    if (!show) {
      this.isPdfLoaded.set(false);
      this.isPdfViewerVisible.set(false);
      return;
    }
    this.scheduleTimeout(() => {
      this.isPdfViewerVisible.set(true);
      this.previewReady.set(false);
      this.schedulePreviewReady();
    }, PDF_VIEWER_REMOUNT_DELAY_MS);
  }

  private scheduleTimeout(callback: () => void, delayMs: number) {
    const handle = setTimeout(() => {
      this.pendingTimeouts.delete(handle);
      callback();
    }, delayMs);
    this.pendingTimeouts.add(handle);
  }
}
